import { AxiosResponse } from 'axios';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { patchGlobalRules } from 'Services';
import { RQ_GLOBAL_RULES, RQ_HISTORY, RQ_PARAMETERS, RQ_RULE_GROUPS, RQ_TENANTS } from 'Utils';

import useGlobalRuleErrorHandler from './useGlobalRuleErrorHandler';

const editGlobalRule = (templateRepositoryId: number, revision?: number) => (
    data: RecursivePartial<GlobalRulesData>
) => {
    return patchGlobalRules({
        templateRepositoryId,
        revision,
        ...data
    });
};

type Options = {
    mode: 'overview' | 'rule';
    ruleId?: string;
    mutationOptions?: UseMutationOptions<AxiosResponse<any>, unknown, RecursivePartial<GlobalRulesData>, unknown>;
    revision?: number;
};

export default function useUpdateGlobalRules(
    templateRepositoryId: number,
    { mode, ruleId, revision, mutationOptions: { onSuccess, onError, ...rest } = {} }: Options
) {
    const queryClient = useQueryClient();
    const errorHandler = useGlobalRuleErrorHandler(templateRepositoryId, {
        mode,
        ruleId
    });

    const ret = useMutation(editGlobalRule(templateRepositoryId, revision), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId]);
            queryClient.invalidateQueries([RQ_RULE_GROUPS]);
            queryClient.invalidateQueries([RQ_HISTORY]);
            // if a tenant overview information changes in impersonation mode
            // we want to show the change in the tenant table in normal mode
            queryClient.invalidateQueries([RQ_TENANTS]);
            queryClient.invalidateQueries([RQ_PARAMETERS]);

            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: async (error: any, variable, context) => {
            errorHandler(error, ret.reset);
            onError?.(error, variable, context);
        },
        ...rest
    });

    return ret;
}
